import { Fragment, useEffect, useMemo, useState, Ref } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { IInitOptions, IProductConfiguration } from '@vox/front-configurator';
interface IProductCardLink {
  type: 'link' | 'content';
  buttonLink: string;
  buttonTarget: '_blank' | '_self';
  buttonText: string;
}
import { useTranslation } from 'next-i18next';
import { IBannersSection, IDescriptionSection, IGallerySection, IMetaData, IProductConfigResponse, IProductsCardSection, IRecommendedProductResponse, IRichContent, ISection } from '@features/productPreview/typescript';
import { WindowType, TRegion } from '@typescript/index';
import { useResponsive } from '@features/responsive';
import { productPageState, uiState } from '@features/productPreview/mobX/state/context';
import ErrorBoundary from '@components/errors/ErrorBoundary';
import { PIMCORE_LOCALE_FALLBACK } from '@config/index';
import Section from '@features/productPreview/sections/ProductDescription/Section';
import GallerySliderFullMode from '@features/productPreview/sections/Gallery/FullMode/GallerySliderFullMode';
import Banners from '@features/productPreview/sections/Banners/Banners';
import ModalRootPortal from '@components/ModalRootPortal/ModalRootPortal';
import getPublicEnv from '@utils/getPublicEnv';
import RichContent from '@features/productPreview/sections/RichContent/RichContent';
import layoutUiState from '@features/layout/mobX/LayoutUiState';
import { TConfiguratorsTypes, IVideoProductSection } from '@features/productPreview/sections/ProductDescription/typescript';
import ProductDescription from '@features/productPreview/sections/ProductDescription/ProductDescription';
import { getRegion } from '@utils/misc';
import useBodyOverflow from '@hooks/useBodyOverflow';
import RecommendedProducts from '@features/productPreview/sections/RecommendedProducts/RecommendedProducts';
import AlgoliaRecommendedProducts from '@features/productPreview/sections/AlgoliaRecommendedProducts/AlgoliaRecommendedProducts';
import Configurator from '@features/productPreview/sections/Configurator/Configurator';
import FrontConfigurator from '@features/productPreview/sections/FrontConfigurator/FrontConfigurator';
import RelatedProducts from '@features/productPreview/sections/RelatedProducts/RelatedProducts';
import { ICmsDocument, ICmsContent } from '@features/cms/typescript';
import SliderLinkedItems from '@components/SliderLinkedItems/SliderLinkedItems';
import TrustMateReviews from '@features/productPreview/components/TrustMateReviews/TrustMateReviews';
export type PropsType = {
  sections: ISection[];
  productID: number;
  configuratorRef: Ref<HTMLElement>;
  metaData: IMetaData;
  richContent: IRichContent | null;
  configuratorData: IProductConfiguration | null;
  configuratorConfig: IInitOptions | null;
  isCreativeProduct?: boolean;
  relatedProductsResponse: IRecommendedProductResponse | null;
  recommendedNonDecorationResponse: IRecommendedProductResponse | null;
  recommendedDecorationResponse: IRecommendedProductResponse | null;
  articles: ICmsDocument[];
  showTrustMateReviews: boolean;
  config: IProductConfigResponse | null;
};
const DynamicInteriors = dynamic(() => import('@features/productPreview/sections/Interiors/Interiors'), {
  ssr: false
});
const ProductPreviewContent = ({
  sections,
  productID,
  configuratorRef,
  metaData,
  richContent,
  configuratorData,
  configuratorConfig,
  isCreativeProduct = false,
  relatedProductsResponse,
  recommendedDecorationResponse,
  articles,
  showTrustMateReviews,
  config
}: PropsType) => {
  const {
    t
  } = useTranslation('common');
  const {
    selectedSlider,
    hideSlider,
    isOpenModal
  } = uiState;
  const {
    locale
  } = useRouter();
  const router = useRouter();
  const [typeOfConfigurator, setTypeOfConfigurator] = useState<TConfiguratorsTypes>('old');
  const region = getRegion('uppercase') as TRegion;
  const [currentView, setCurrentView] = useState<WindowType | null>(null);
  const [sectionProductsCard, setSectionProductsCard] = useState<IProductsCardSection | undefined>(undefined);
  const [productCardLinksDocuments, setProductCardLinksDocuments] = useState<Record<number, ICmsContent[]>>([]);
  const [productCardTitles, setProductCardTitles] = useState<string[]>([]);
  const {
    isMobile,
    isMobilelg,
    isTablet,
    isTabletlg,
    isWiderThanDesktop,
    currentBreakpoint
  } = useResponsive();
  const {
    setIsProductCardOpened
  } = layoutUiState;
  const {
    setIsOldConfigurator
  } = productPageState;
  const language = locale ? PIMCORE_LOCALE_FALLBACK[locale] : PIMCORE_LOCALE_FALLBACK[getPublicEnv('NEXT_PUBLIC_DEFAULT_LOCALE')];
  useBodyOverflow(isOpenModal, 'modal', true, true);
  useEffect(() => {
    if (isMobile || isMobilelg) {
      setCurrentView(WindowType.mobile);
    } else if (isTablet || isTabletlg) {
      setCurrentView(WindowType.tablet);
    } else if (isWiderThanDesktop) {
      setCurrentView(WindowType.desktop);
    } else {
      setCurrentView(WindowType.desktop);
    }
  }, [currentBreakpoint, isMobile, isMobilelg, isTablet, isTabletlg, isWiderThanDesktop]);
  useEffect(() => {
    const handleRouteChange = () => {
      hideSlider();
    };
    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router, hideSlider]);
  useEffect(() => {
    if (configuratorData && configuratorConfig) {
      setTypeOfConfigurator('front');
    }
  }, [configuratorData, configuratorConfig]);
  useEffect(() => {
    setIsOldConfigurator(typeOfConfigurator === 'old');
  }, [setIsOldConfigurator, typeOfConfigurator]);
  useEffect(() => {
    setIsProductCardOpened(true);
    return () => {
      setIsProductCardOpened(false);
    };
  }, [setIsProductCardOpened]);

  // lastVisitedProductId is handled in pages/product/[slug].tsx (beforePopState).
  // Do not clear/register it here — observer + lastVisitedProductId in deps was wiping the id on back.
  const relatedProducts = useMemo(() => productID && typeOfConfigurator !== 'front' ? <RelatedProducts mainProductID={productID} windowType={currentView ?? WindowType.mobile} initialResponse={relatedProductsResponse} /> : null, [productID, typeOfConfigurator, currentView, relatedProductsResponse]);
  const recommendedProducts = useMemo(() => productID ? <AlgoliaRecommendedProducts mainProductID={productID} windowType={currentView ?? WindowType.mobile} key={`recommendedProducts-${productID}`} /> : null, [productID, currentView]);
  const renderArticles = useMemo(() => articles && articles.length > 0 ? <SliderLinkedItems title={t('linkedArticles')} articles={articles} /> : null, [articles, t]);
  const recommendedDecorations = useMemo(() => productID ? <RecommendedProducts isDecorativeArticle mainProductID={productID} windowType={currentView ?? WindowType.mobile} key={`recommendedDecorations-${productID}`} initialResponse={recommendedDecorationResponse} /> : null, [productID, currentView, recommendedDecorationResponse]);
  const interiors = useMemo(() => productID && region === 'PL' ? <DynamicInteriors productId={productID} /> : null, [productID, region]);
  const gpsrItem = useMemo(() => {
    if (!sections || !sections.length) {
      return null;
    }
    const productDescriptionSection = sections.find((section): section is IDescriptionSection => section.type === 'PRODUCT_DESCRIPTIONS');
    if (!productDescriptionSection) {
      return null;
    }
    return {
      energyClassIcon: productDescriptionSection.items.find(item => item.descriptionCode === 'PRODUCT_ENERGY_CLASS')?.files?.[0]?.thumb || null,
      energyClassPage: productDescriptionSection.items.find(item => item.descriptionCode === 'PRODUCT_INFORMATION_PAGE_URL')?.files?.[0]?.url || null,
      informationSheet: productDescriptionSection.items.find(item => item.descriptionCode === 'PRODUCT_INFORMATION_SHEET')?.files?.[0]?.url || null,
      informationLabelUrl: productDescriptionSection.items.find(item => item.descriptionCode === 'PRODUCT_INFORMATION_LABEL_URL')?.files?.[0]?.url || null
    };
  }, [sections]);
  useEffect(() => {
    if (!sectionProductsCard) {
      const newSectionProductsCard = sections.find((section): section is IProductsCardSection => section.type === 'PRODUCT_PRODUCTS_CARD');
      if (newSectionProductsCard) {
        setSectionProductsCard(newSectionProductsCard);
      }
    }
  }, [sections, sectionProductsCard]);

  // Przetwarzanie asynchroniczne bloków
  useEffect(() => {
    if (!sectionProductsCard) return;
    const processBlocks = () => {
      const documents: Record<number, ICmsContent[]> = [];
      const titles: string[] = [];
      sectionProductsCard.items.forEach((item, index) => {
        const documentsInItem: ICmsContent[] = [];
        titles.push(item?.buttonText ?? '');
        if (item?.blocks && item.blocks.length > 0) {
          item.blocks.forEach(blocksData => {
            Object.values(blocksData).forEach(blocks => {
              blocks.forEach(block => {
                documentsInItem.push({
                  ...block.data,
                  type: block.type,
                  code: block.code ?? ''
                } as ICmsContent);
              });
            });
          });
        }
        documents[index] = documentsInItem;
      });
      setProductCardLinksDocuments(documents);
      setProductCardTitles(titles);
    };
    processBlocks();
  }, [sectionProductsCard]);
  const productCardLinks: IProductCardLink[] | null = useMemo(() => {
    if (!sections || !sections.length || !sectionProductsCard) {
      return null;
    }
    const items = sectionProductsCard.items;
    if (!items || !items.length) {
      return null;
    }
    const links = items.map((item, index) => {
      if (!item || !item.buttonText) return null;
      const type = item?.buttonLink ? 'link' : 'content';
      const buttonTarget: '_blank' | '_self' = type === 'link' && item?.buttonTarget === '_blank' ? '_blank' : '_self';
      return {
        type: type,
        buttonLink: type === 'link' ? item?.buttonLink ?? '' : String(index),
        buttonTarget,
        buttonText: item.buttonText
      };
    }).filter((link): link is IProductCardLink => link !== null);
    return links;
  }, [sections, sectionProductsCard]);
  useEffect(() => {
    if (!configuratorConfig) return;
    const config = configuratorConfig as IInitOptions & {
      productCardLinks?: IProductCardLink[];
      energyClassIconUrl?: string;
      energyClassPageUrl?: string;
      informationSheetUrl?: string;
      informationLabelUrl?: string;
    };
    if (gpsrItem?.energyClassIcon) config.energyClassIconUrl = gpsrItem.energyClassIcon;
    if (gpsrItem?.energyClassPage) config.energyClassPageUrl = gpsrItem.energyClassPage;
    if (gpsrItem?.informationSheet) config.informationSheetUrl = gpsrItem.informationSheet;
    if (gpsrItem?.informationLabelUrl) config.informationLabelUrl = gpsrItem.informationLabelUrl;
    if (productCardLinks) config.productCardLinks = productCardLinks;
  }, [sections, configuratorConfig, gpsrItem, productCardLinks]);
  const stickyBarLinks: Array<string> = [];
  const isProductGalleryType = (section: ISection): section is IGallerySection => section.type === 'PRODUCT_SLIDES';
  const isVideoProductType = (section: ISection): section is IVideoProductSection => section.type === 'VIDEO_PRODUCT';
  const isDescriptionType = (section: ISection) => section.type === 'PRODUCT_DESCRIPTIONS';
  const isBannersPromotionType = (section: ISection): section is IBannersSection => section.type === 'PRODUCT_BANNERS_PROMOTION';
  const renderSections = sections.length > 0 && sections.map(section => {
    stickyBarLinks.push(section.type);
    if (isDescriptionType(section)) return <Fragment key={`PRODUCT_DESCRIPTIONS_${productID}`}>
            <Section section={section} windowType={currentView || WindowType.mobile} typeofconfigurator={typeOfConfigurator} isCreativeProduct={isCreativeProduct} />
            {showTrustMateReviews && metaData.trustmateData && metaData.trustmateData.review.length > 0 && <TrustMateReviews trustmateData={metaData.trustmateData} />}
            {richContent && richContent.html !== '' && <RichContent key="richContent" html={richContent.html} allowRerender />}
          </Fragment>;
    if (section.type === 'PRODUCT_SLIDES' && typeOfConfigurator !== 'old') return null;
    if (isProductGalleryType(section) && selectedSlider && typeOfConfigurator === 'old' && currentView === WindowType.desktop && isOpenModal) return <ModalRootPortal key={section.type}>
            <GallerySliderFullMode selectedSlider={selectedSlider} slides={section.items[0].slides} />
          </ModalRootPortal>;
    if (isBannersPromotionType(section)) {
      return <Banners key={`PRODUCT_BANNERS_PROMOTION${productID}`} items={section.items.filter(item => item.code === 'productCardBanner')} hrefPrefix="/pages/" windowType={currentView || WindowType.desktop} />;
    }
    if (isVideoProductType(section)) {
      return <section key={`VIDEO_PRODUCT_${productID}`} sx={{
        mb: 32
      }}>
            <ProductDescription section={section} windowType={currentView || WindowType.mobile} hiddenTitle isCreativeProduct={isCreativeProduct} />
          </section>;
    }
    return <Section key={`${section.type}_${productID}`} section={section} windowType={currentView || WindowType.mobile} typeofconfigurator={typeOfConfigurator} isCreativeProduct={isCreativeProduct} />;
  });
  const renderProperConfigurator = () => configuratorData && configuratorConfig ? <FrontConfigurator configuratorData={configuratorData} configuratorConfig={configuratorConfig} ref={configuratorRef as Ref<HTMLDivElement>} productCardLinksDocuments={productCardLinksDocuments} productCardTitles={productCardTitles} config={config} /> : <Configurator ref={configuratorRef as Ref<HTMLDivElement>} productID={productID} lang={language} metaData={metaData} />;
  return <>
      {productID && renderProperConfigurator()}
      <ErrorBoundary>{renderSections}</ErrorBoundary>
      {interiors}
      {relatedProducts}
      {recommendedProducts}
      {recommendedDecorations}
      {renderArticles}
    </>;
};
export default observer(ProductPreviewContent);